import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Send, X, Loader2, MessageSquare, Sparkles, ChevronDown, Plus, RefreshCw, ArrowRight, Compass, Link2, Check, AlertCircle } from "lucide-react";
import { cn } from "utils/cn";
import { useAppDispatch, useAppSelector, useAppStore } from "store";
import { setChatbotOpen } from "store/commonSlice";
import { addChatbotMessages, appendChatbotChunk, failChatbotMessage, finishChatbotMessage, noteChatbotChunkIndex, setChatbotMessages, startChatbotTurn, type ChatMessage } from "store/chatbotSlice";
import chatbotService from "api/chatbot/chatbot.service";
import { IAskChatbotRequest, IChatbotHistoryMessage, IChatbotNavAction } from "api/chatbot/chatbot.type";
import HttpClient from "api/HttpClient";
import { SocketContext } from "context/SocketContext";
import { trackEvent } from "utils/analytics";
import { EVENTS } from "utils/analytics.events";
import { detectQueryLanguage } from "utils/detectQueryLanguage";
import { confirmNavigation } from "utils/unsavedChangesGuard";
import { useMediaQuery } from "hooks/useMediaQuery";
import { findGuidedFlow, useGuideCoordinator } from "hooks/useGuideCoordinator";
import { getPageContext } from "./pageContext";
import { filterNavActions, isGuidedActionEligible, isWorkflowSupported, type GuidedAccessState } from "./navActions";
import { buildReviewCapture, isReviewable } from "./reviewCapture";
import { ChatbotProseLink } from "./messageLinks";
import { GuideCard, GuideChip } from "./GuideCard";
import "./chatbot.css";
import { httpStatusOf } from "utils/httpStatusOf";

const NO_CHUNK_TIMEOUT_MS = 30_000;
// Multi-turn follow-ups: replay the most recent done exchanges so the BE has
// context for "explain more" / "step one" style asks. BE caps at 12; sending 6
// keeps us well under both that and the formatted-block budget.
const HISTORY_MAX_MESSAGES = 6;
const HISTORY_MAX_CONTENT_CHARS = 2000;

const DashboardChatbot = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const dispatch = useAppDispatch();
  // Live store read, not a selector snapshot. The WS effect and the watchdog must decide against
  // the session the store holds *now*: a chunk can land in the same tick a turn starts, and
  // `react-hooks/exhaustive-deps` is off in this repo, so a captured value is a real stale-closure
  // hazard rather than a theoretical one. This is the sanctioned `useAppStore()` case (CLAUDE.md).
  const store = useAppStore();
  const readTurn = useCallback(() => store.getState().chatbot, [store]);
  const isOpen = useAppSelector((s) => s.common.isChatbotOpen);
  // On mobile the chatbot overlays the page (fixed) instead of squeezing the layout like the desktop side panel.
  const isMobile = useMediaQuery("(max-width: 833px)");
  const currentPageState = useAppSelector((s) => s.common.currentPageState);
  const socket = useContext(SocketContext);
  const lastMessage = socket?.lastMessage;

  const close = useCallback(() => dispatch(setChatbotOpen(false)), [dispatch]);

  // Unconditionally, at the top of the always-mounted component: `Layout` renders this panel
  // whenever the user is authorized regardless of `isOpen` (`Layout.tsx:129`), and the root element
  // below is merely collapsed to zero width when closed. Putting this behind the open check — or
  // inside the panel's JSX — would stop the coordinator observing the feature's report while the
  // panel is shut, so an extraction that finished in the meantime would never latch as completed.
  const { guide, start: startGuide, exit: exitGuide } = useGuideCoordinator();

  const navigate = useNavigate();
  const permissions = useAppSelector((s) => s.common.user.permissions);
  const packageCode = useAppSelector((s) => s.common.user.packageCode);
  const hasSession = useAppSelector((s) => s.common.isAuthorized);

  // Permission filtering lives in a pure helper (navActions.ts) with its own tests: the two
  // permission defaults are easy to invert, and inverting them offers buttons into features
  // the user's plan excludes. Applied at RENDER time so a mid-session permission change
  // takes effect instead of leaving a stale button on an old message.
  const visibleActionsFor = useCallback((m: ChatMessage) => filterNavActions(m.actions, permissions), [permissions]);

  // Everything the guided-offer gate needs about this user, assembled once rather than per
  // action per render. Same three values the route guard reads (`AuthorizationPage.tsx`).
  const guidedAccess = useMemo<GuidedAccessState>(() => ({ permissions, packageCode, hasSession }), [permissions, packageCode, hasSession]);

  /**
   * The guided flow behind an action, or null — the whole "may we offer this" decision (REF-6217
   * Phase 2 / T2.4), composed from T2.3's two pure predicates:
   *
   *   `isWorkflowSupported`  — does THIS BUILD know the flow the backend named?
   *   `isGuidedActionEligible` — can THIS USER actually enter it?
   *
   * Both must hold, and both are answered here rather than at click time, so a Guide button never
   * appears that cannot deliver. Every degraded case simply renders the normal Go button alone.
   * No capability id, workflow id or permission name appears in this component.
   */
  const guidedFlowFor = useCallback(
    (action: IChatbotNavAction) => {
      const definition = findGuidedFlow(action.workflow_id ?? "");
      if (!isWorkflowSupported(action, definition)) return null;

      return isGuidedActionEligible(action, guidedAccess) ? definition : null;
    },
    [guidedAccess]
  );

  const goToCapability = useCallback(
    async (action: IChatbotNavAction) => {
      // Same posture as every other programmatic nav surface in the app (`useMenuItems.ts:146`,
      // `SideMenu`, `MobileNavDrawer`, Tech Pack's own back arrow): ask the unsaved-changes guard
      // first and bail out completely when the user chooses to stay — analytics and the mobile
      // panel close included, so a canceled navigation leaves no trace. Without this the panel
      // was the one surface that could navigate a dirty Tech Pack editor away silently.
      // Concurrent clicks need no handling here: the guard settles an earlier pending request as
      // canceled when a new one arrives (`TechPackResult.tsx`), and it never rejects.
      if (!(await confirmNavigation())) return;

      trackEvent(EVENTS.CHATBOT.NAV_CLICK, { capability_id: action.capability_id, route: action.route });
      // Closing first keeps the destination page full-width on mobile, where the panel
      // overlays rather than squeezes the layout.
      if (isMobile) dispatch(setChatbotOpen(false));
      navigate(action.route);
    },
    [dispatch, isMobile, navigate]
  );

  /**
   * "Guide me through it" — the Phase 2 handoff into the existing Phase 1 runtime (REF-6217 T2.4).
   *
   * Order is the whole contract, and each step is here for a reason:
   *
   * 1-2. Re-resolve and re-check. The button's own render already proved both, but actions live in
   *      `chatbotSlice` and outlive the answer that produced them: permissions can change, a
   *      package can change, and a deploy can move the definition's version under a message that
   *      is still on screen. Render-time filtering is necessary and not sufficient.
   * 3.   `confirmNavigation()` BEFORE anything observable. A cancelled confirm must leave no
   *      trace — no run, no `guide_started`, no closed panel, no navigation.
   * 4.   Mobile panel close, matching `goToCapability` exactly. Safe before `start` because the
   *      coordinator is mounted above the panel-open conditional.
   * 5.   `start` before `navigate`, and synchronously: `navigate` remounts `Layout` and this
   *      component with it, so anything scheduled after it would run in an unmounting tree.
   * 6.   `definition.entry`, NOT `action.route` — see the comment on the navigate call.
   */
  const startGuidedFlow = useCallback(
    async (action: IChatbotNavAction) => {
      const definition = guidedFlowFor(action);
      if (!definition) return;

      if (!(await confirmNavigation())) return;

      if (isMobile) dispatch(setChatbotOpen(false));
      // Idempotent for a run of this flow that is already active: the coordinator keeps the
      // original record and emits no second `guide_started` (`useGuideCoordinator.ts`), and a
      // completed run starts fresh. Either way the user asked to be taken to the flow's page, so
      // navigation proceeds regardless of what `start` reports — the boolean means "is this a flow
      // I know", which `isWorkflowSupported` has already established.
      startGuide(definition.id, { source: "chatbot" });
      // The flow's OWN entry, not the capability's nav route. For Tech Pack those differ:
      // `action.route` is /tech-packs (the project list) while the guided flow begins at
      // /tech-pack (the create wizard). Using the action's route here would start the guide and
      // then land the user somewhere it has nothing to say about.
      navigate(definition.entry);
    },
    [guidedFlowFor, isMobile, dispatch, startGuide, navigate]
  );

  /**
   * Back to the guided flow's own page from the away state. Same guard posture as
   * `goToCapability` above — a dirty Tech Pack editor gets its confirm, and cancelling leaves the
   * user exactly where they are, still away, with the run untouched.
   *
   * Deliberately does NOT restart anything: the flow is already active, so there is no
   * `start()` call and no analytics here. `chatbot_nav_click` stays what it is — the capability
   * graph's outcome metric — and the guide funnel's events are all the coordinator's.
   */
  const returnToGuide = useCallback(
    async (entry: string) => {
      if (!(await confirmNavigation())) return;

      if (isMobile) dispatch(setChatbotOpen(false));
      navigate(entry);
    },
    [dispatch, isMobile, navigate]
  );

  // Redux, not local state: `Layout` — and with it this panel — is re-instantiated by every
  // route (`Router.tsx::getRoute`), so a local conversation was destroyed by the very navigation
  // the answer asked for (REF-6217 T0.1). The store is the single owner; `messagesRef` below is
  // only a read mirror for the history builder.
  const messages = useAppSelector((s) => s.chatbot.messages);
  const [input, setInput] = useState("");
  // P1-1: the in-flight turn lives in the store, not local state. A cross-layout navigation
  // (`Layout` → `AcademyLayout`) unmounts this component, and a local flag came back false while
  // the BE was still streaming — so the panel forgot it had a turn running and the answer was
  // thrown away. Derived, never set directly: the slice's terminal reducers release the slot.
  const activeSessionId = useAppSelector((s) => s.chatbot.activeSessionId);
  const isStreaming = activeSessionId !== null;
  const [sessionMenuOpen, setSessionMenuOpen] = useState(false);
  // Review capture (REF-6217 P1-0). Server-authoritative: we render the affordance only
  // when the BE says this account is on the reviewer allowlist, and the BE re-checks on
  // create — this flag is presentation, never the gate.
  const [reviewEligible, setReviewEligible] = useState(false);
  const [capturingId, setCapturingId] = useState<string | null>(null);
  // Outcome of the last capture, shown ON the button. The Router's snackbar handler only
  // understands types 402/409/503 (see docs/rules/snackbar.md) — there is no success toast in
  // this app at all — so the dispatch this used to rely on fell into `default: break` and the
  // reviewer got NO feedback for a capture that had actually succeeded. Confirmation belongs
  // where the click happened anyway.
  const [captureResult, setCaptureResult] = useState<{ id: string; state: "copied" | "id-only" | "failed" } | null>(null);

  const inputRef = useRef<HTMLTextAreaElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const sessionMenuRef = useRef<HTMLDivElement>(null);
  const messagesRef = useRef<ChatMessage[]>([]);
  const canceledSessionIdsRef = useRef<Set<string>>(new Set());
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Indirection so the mount-only adopt effect can reach `markInterrupted`, which is declared
  // further down. Kept current by the effect right after that declaration.
  const markInterruptedRef = useRef<(sessionId: string) => void>(() => undefined);
  const abortControllerRef = useRef<AbortController | null>(null);
  const isMountedRef = useRef(true);
  // Per-send analytics metadata. Carried across the async send → stream
  // lifecycle so the chatbot_first_chunk / _done / _cancel events can
  // report the originating page_route, query_language, latency.
  const sendMetaRef = useRef<{
    sessionId: string;
    startedAt: number;
    language: "tr" | "en";
    pageRoute: string;
    firstChunkFired: boolean;
    chunkCount: number;
  } | null>(null);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
      // Only this mount's timer is ours to drop. The turn itself belongs to the store now, and the
      // next mount re-arms the watchdog below.
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  // P1-1. An unmount used to mean "this turn is dead": it aborted the request and marked the
  // bubble `interrupted`. But the panel is re-instantiated by every LAYOUT change
  // (`Router.tsx::getRoute` nests it in each route's own layout, so `Layout` → `AcademyLayout`
  // swaps the component type and React drops the subtree), while the BE went on streaming and
  // delivered a complete answer nobody could see. Worse, `interrupted` is sticky — the slice only
  // writes into a bubble that is still `streaming` — so the real answer could never land, and
  // `buildHistory` then replayed the orphaned question on every later turn.
  //
  // So a remount now ADOPTS the running turn instead of killing it: the WS is app-global and the
  // frames keep arriving, all this mount has to restore is the timer that catches a genuinely
  // dead stream. Aborting the HTTP request was never load-bearing either — `/v1/chatbot/ask` is a
  // kickoff that returns immediately and the BE task is detached.
  useEffect(() => {
    const { activeSessionId: adopted } = readTurn();
    if (!adopted) return;

    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => markInterruptedRef.current(adopted), NO_CHUNK_TIMEOUT_MS);
    // Mount-only, deliberately: re-arming on every render would push the deadline out forever.
    // (`react-hooks/exhaustive-deps` is not even registered in this repo's config, so there is no
    // disable comment to add here — the empty dep array is the whole statement of intent.)
  }, []);

  const user = useAppSelector((s) => s.common.user);
  const firstName = useMemo(() => {
    const name = (user as { name?: string } | undefined)?.name;
    if (!name) return "";

    return name.trim().split(/\s+/)[0] || "";
  }, [user]);

  const buildIntroMessage = useCallback(
    (pathname: string): ChatMessage => {
      const ctx = getPageContext(pathname);
      // When the user has no first name, render the *_no_name intro variant so
      // we don't get awkward fallback rendering like TR "Merhaba {fallback}!".
      const text = firstName ? t(ctx.introKey, { name: firstName }) : t(`${ctx.introKey}_no_name`);

      return { id: uuidv4(), role: "assistant", text, status: "done", sessionId: null, isIntro: true, suggestions: ctx.suggestions };
    },
    [t, firstName]
  );

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [messages, isStreaming, isOpen]);

  useEffect(() => {
    if (isOpen) inputRef.current?.focus();
  }, [isOpen]);

  // Asked once per authorized session, on first open rather than on mount: the panel is
  // always mounted (Layout renders it whenever the user is authorized), so keying this on
  // mount would put a request on every page load for a feature almost nobody has. Failure
  // is silent and leaves the affordance hidden — the safe direction for a capture control.
  const eligibilityAskedRef = useRef(false);
  useEffect(() => {
    if (!isOpen || !hasSession || eligibilityAskedRef.current) return;
    eligibilityAskedRef.current = true;

    // `await` rather than `.then()`: HttpClient's interceptor can resolve a handled
    // response, and a service that returns nothing at all must not throw inside an
    // effect and take the whole panel down with it. Any failure means "not eligible".
    (async () => {
      try {
        const res = await chatbotService.getReviewEligibility();
        setReviewEligible(Boolean(res?.responseData?.eligible));
      } catch {
        setReviewEligible(false);
      }
    })();
  }, [isOpen, hasSession]);

  // Seeded with the CURRENT open state, not `false`: `isOpen` lives in Redux and survives the
  // per-route remount, so a `false` seed made every navigation look like a fresh open — firing a
  // second `chatbot_open` and (before the store move) wiping the conversation.
  const wasOpenRef = useRef(isOpen);
  useEffect(() => {
    // Seed the intro only into an EMPTY conversation, so remounting on a new route — or
    // reopening the panel — resumes what was already said. "New chat" is the reset affordance.
    if (isOpen && messagesRef.current.length === 0) dispatch(setChatbotMessages([buildIntroMessage(location.pathname)]));

    if (isOpen && !wasOpenRef.current) {
      setInput("");
      trackEvent(EVENTS.CHATBOT.OPEN, { page_route: location.pathname });
    } else if (!isOpen && wasOpenRef.current) {
      trackEvent(EVENTS.CHATBOT.CLOSE, { page_route: location.pathname });
    }
    wasOpenRef.current = isOpen;
  }, [isOpen, buildIntroMessage, location.pathname, dispatch]);

  // While the panel is open and the user hasn't engaged yet (only the intro is on screen),
  // re-anchor the intro to the new page when the route changes.
  useEffect(() => {
    if (!isOpen) return;
    const prev = messagesRef.current;
    const onlyIntro = prev.length === 1 && prev[0].isIntro;
    if (!onlyIntro) return;

    const next = buildIntroMessage(location.pathname);
    // Skip a same-copy rewrite: two paths can share one page context (/tech-pack vs
    // /tech-pack/:id), and re-dispatching a fresh object for an identical intro would re-render
    // for nothing — and loop outright if `buildIntroMessage` ever loses its identity stability.
    if (prev[0].text === next.text) return;

    dispatch(setChatbotMessages([next]));
  }, [location.pathname, isOpen, buildIntroMessage, dispatch]);

  // Keep the WS session warm while the panel is open. The BE closes idle
  // sessions after ~5min; without this, a follow-up sent after a long pause
  // sees its chunks land on a dead session and renders as "interrupted".
  useEffect(() => {
    if (!isOpen || !socket?.ping) return;
    const interval = setInterval(() => socket.ping(), 60_000);

    return () => clearInterval(interval);
  }, [isOpen, socket]);

  const buildHistory = useCallback((): IChatbotHistoryMessage[] => {
    const history: IChatbotHistoryMessage[] = [];

    // P1-1: replay COMPLETED EXCHANGES, not surviving messages. Filtering message-by-message on
    // `status === "done"` drops an interrupted/canceled/errored assistant but keeps its user
    // question, which is `done` — so the model saw a question it had never answered and resolved
    // the next "that"/"it" against it. Pairing by `sessionId` is what makes that structural: a turn
    // is in the history only if BOTH halves completed, wherever it sits in the conversation. An
    // earlier attempt only dropped a TRAILING orphan, which just delayed the damage by one turn.
    const answered = new Set(messagesRef.current.filter((m) => m.role === "assistant" && !m.isIntro && m.status === "done" && m.text && m.sessionId).map((m) => m.sessionId));

    for (const m of messagesRef.current) {
      if (m.isIntro) continue;
      if (m.role !== "user" && m.role !== "assistant") continue;
      if (m.status !== "done") continue;
      if (!m.text) continue;
      if (!m.sessionId || !answered.has(m.sessionId)) continue;
      history.push({ role: m.role, content: m.text.slice(0, HISTORY_MAX_CONTENT_CHARS) });
    }

    return history.slice(-HISTORY_MAX_MESSAGES);
  }, []);

  const markInterrupted = useCallback(
    (sessionId: string) => {
      if (!isMountedRef.current || readTurn().activeSessionId !== sessionId) return;
      dispatch(finishChatbotMessage({ sessionId, status: "interrupted" }));
      // P1-1: this is the one place the FE genuinely gives up on a turn, so it is the one place
      // that owes the BE a cancel. Without it the abandoned generation ran to completion — still
      // calling Vertex, still billing — beside whatever the user asked next. A remount is NOT an
      // abandonment any more, which is why the cancel lives here and not in the unmount path.
      chatbotService.cancelStream({ session_id: sessionId }).catch(() => undefined);
      const meta = sendMetaRef.current;
      if (meta && meta.sessionId === sessionId) {
        trackEvent(EVENTS.CHATBOT.ABANDON, {
          page_route: meta.pageRoute,
          query_language: meta.language,
          duration_ms: Date.now() - meta.startedAt,
          chunk_count: meta.chunkCount,
          first_chunk_fired: meta.firstChunkFired,
        });
        sendMetaRef.current = null;
      }
    },
    [dispatch, readTurn]
  );

  // Keeps the mount-only adopt effect above pointing at the current closure.
  useEffect(() => {
    markInterruptedRef.current = markInterrupted;
  }, [markInterrupted]);

  const sendMessage = useCallback(
    async (overrideText?: string) => {
      const raw = overrideText ?? input;
      const text = raw.trim();
      if (!text || isStreaming) return;

      abortControllerRef.current?.abort();
      const controller = new AbortController();
      abortControllerRef.current = controller;

      const mySessionId = uuidv4();
      const userMsgId = uuidv4();
      const assistantMsgId = uuidv4();
      const history = buildHistory();
      const language = detectQueryLanguage(text);
      const pageRoute = location.pathname;

      sendMetaRef.current = {
        sessionId: mySessionId,
        startedAt: Date.now(),
        language,
        pageRoute,
        firstChunkFired: false,
        chunkCount: 0,
      };
      trackEvent(EVENTS.CHATBOT.SEND, {
        page_route: pageRoute,
        query_language: language,
        query_length: text.length,
        history_count: history.length,
      });

      dispatch(startChatbotTurn({ sessionId: mySessionId }));
      setInput("");
      dispatch(
        addChatbotMessages([
          { id: userMsgId, role: "user", text, status: "done", sessionId: mySessionId },
          { id: assistantMsgId, role: "assistant", text: "", status: "streaming", sessionId: mySessionId },
        ])
      );

      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => markInterrupted(mySessionId), NO_CHUNK_TIMEOUT_MS);

      try {
        const payload: IAskChatbotRequest = {
          session_id: mySessionId,
          message: text,
          ...(history.length > 0 ? { history } : {}),
          ...(currentPageState ? { page_state: currentPageState } : {}),
        };
        const res = await chatbotService.askQuestion(payload, { signal: controller.signal });

        if (!isMountedRef.current || readTurn().activeSessionId !== mySessionId) return;

        if (res?._handled) {
          if (timeoutRef.current) clearTimeout(timeoutRef.current);
          dispatch(failChatbotMessage({ id: assistantMsgId, fallbackText: "Session ended" }));
          trackEvent(EVENTS.CHATBOT.ERROR, {
            page_route: pageRoute,
            query_language: language,
            error_kind: "session_ended",
          });
          sendMetaRef.current = null;
        }
      } catch (err) {
        if (HttpClient.isCancel(err as Error)) return;
        if (!isMountedRef.current || readTurn().activeSessionId !== mySessionId) return;
        if (timeoutRef.current) clearTimeout(timeoutRef.current);

        const status = (err as { response?: { status?: number } })?.response?.status;
        const errorMsg = status === 402 ? "Out of credits" : "Couldn't send your question. Please try again.";

        dispatch(failChatbotMessage({ id: assistantMsgId, text: errorMsg }));
        trackEvent(EVENTS.CHATBOT.ERROR, {
          page_route: pageRoute,
          query_language: language,
          error_kind: status === 402 ? "out_of_credits" : "http_error",
          http_status: httpStatusOf(err) ?? null,
        });
        sendMetaRef.current = null;
      }
    },
    [input, isStreaming, buildHistory, markInterrupted, currentPageState, location.pathname, dispatch]
  );

  /**
   * Snapshot one answer for human review and put the Backoffice link on the clipboard
   * (REF-6217 P1-0).
   *
   * This is the ONLY path that sends conversation text anywhere but `/ask` — nothing is
   * persisted server-side until someone clicks this. Deliberately does not touch
   * `chatbot.messages`: a failed capture is a failed side errand, and it must not be able
   * to mark a bubble errored or disturb an in-flight stream. The reviewer just sees a
   * snackbar and can retry.
   */
  const captureForReview = useCallback(
    async (messageId: string) => {
      const payload = buildReviewCapture(messages, messageId, { pageRoute: location.pathname, uiLocale: i18n.language });
      if (!payload || capturingId) return;

      setCapturingId(messageId);
      setCaptureResult(null);
      try {
        const res = await chatbotService.createReviewCase(payload);
        const caseId = res?.responseData?.case_id;

        if (res?._handled || !caseId) throw new Error("capture_failed");

        // `review_url` is built SERVER-SIDE and copied verbatim. Never construct it from
        // `window.location.origin`: the review page lives in backoffice-v2, on a
        // `*.a.run.app` host with no domain mapping, which is a different origin from
        // this app (app./dev./stage.refabric.com) in every environment. Only the API
        // knows which backoffice instance reads the database it just wrote to.
        const url = res.responseData.review_url;

        // Clipboard can reject (permissions, insecure context). The link is useless to the
        // reviewer if they can't get at it, so a copy failure is a capture failure here.
        //
        // The `|| caseId` branch is DEFENSIVE, not an expected outcome: the BE now refuses
        // capture outright (400) in an environment with no usable backoffice URL, and
        // `/review-eligibility` reports false there so this button isn't even rendered. It
        // survives only to keep a new FE safe against a backend that predates that refusal
        // — in which case the case exists and handing over the id beats inventing a URL.
        await navigator.clipboard.writeText(url || caseId);
        setCaptureResult({ id: messageId, state: url ? "copied" : "id-only" });
        trackEvent(EVENTS.CHATBOT.REVIEW_CAPTURE, { page_route: location.pathname, has_nav: Boolean(payload.nav_capability), has_link: Boolean(url) });
      } catch {
        setCaptureResult({ id: messageId, state: "failed" });
      } finally {
        setCapturingId(null);
      }
    },
    [messages, location.pathname, capturingId, i18n.language]
  );

  const cancelStream = useCallback(() => {
    const sid = readTurn().activeSessionId;
    if (!sid) return;

    canceledSessionIdsRef.current.add(sid);
    abortControllerRef.current?.abort();
    if (timeoutRef.current) clearTimeout(timeoutRef.current);

    dispatch(finishChatbotMessage({ sessionId: sid, status: "canceled" }));

    const meta = sendMetaRef.current;
    if (meta && meta.sessionId === sid) {
      trackEvent(EVENTS.CHATBOT.CANCEL, {
        page_route: meta.pageRoute,
        query_language: meta.language,
        duration_ms: Date.now() - meta.startedAt,
        chunk_count: meta.chunkCount,
        first_chunk_fired: meta.firstChunkFired,
      });
      sendMetaRef.current = null;
    }

    chatbotService.cancelStream({ session_id: sid }).catch(() => undefined);
  }, [dispatch]);

  // WS streaming: progressively update the in-flight assistant bubble as
  // chatbot_chunk frames arrive. See `messageHash.ts` and the BE
  // `docs/chatbot_pipeline.md`.
  useEffect(() => {
    const msg = lastMessage;
    if (!msg || msg.type !== "chatbot_chunk") return;
    if (canceledSessionIdsRef.current.has(msg.session_id)) return;
    const turn = readTurn();
    if (msg.session_id !== turn.activeSessionId) return;
    if (typeof msg.chunk_index !== "number") return;
    if (msg.chunk_index <= turn.lastChunkIndex) return;

    dispatch(noteChatbotChunkIndex({ sessionId: msg.session_id, chunkIndex: msg.chunk_index }));

    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => markInterrupted(msg.session_id), NO_CHUNK_TIMEOUT_MS);

    // Per-session analytics bookkeeping.
    const meta = sendMetaRef.current;
    if (meta && meta.sessionId === msg.session_id) {
      meta.chunkCount += 1;
      if (!meta.firstChunkFired) {
        meta.firstChunkFired = true;
        trackEvent(EVENTS.CHATBOT.FIRST_CHUNK, {
          page_route: meta.pageRoute,
          query_language: meta.language,
          time_to_first_chunk_ms: Date.now() - meta.startedAt,
        });
      }
    }

    if (msg.status === "streaming") {
      dispatch(appendChatbotChunk({ sessionId: msg.session_id, text: msg.message ?? "" }));
    } else if (msg.status === "done") {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      // `actions` only ever arrives on this terminal frame. Filtered by the user's own
      // permissions below at render time — not here — so a permission change mid-session
      // can't leave a stale button attached to an old message.
      const actions = Array.isArray(msg.actions) ? msg.actions : undefined;
      dispatch(finishChatbotMessage({ sessionId: msg.session_id, status: "done", appendText: msg.message ?? "", actions }));
      if (meta && meta.sessionId === msg.session_id) {
        trackEvent(EVENTS.CHATBOT.DONE, {
          page_route: meta.pageRoute,
          query_language: meta.language,
          duration_ms: Date.now() - meta.startedAt,
          chunk_count: meta.chunkCount,
        });
        sendMetaRef.current = null;
      }
    } else if (msg.status === "error") {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      dispatch(finishChatbotMessage({ sessionId: msg.session_id, status: "error", text: msg.error || "Something went wrong" }));
      if (meta && meta.sessionId === msg.session_id) {
        trackEvent(EVENTS.CHATBOT.ERROR, {
          page_route: meta.pageRoute,
          query_language: meta.language,
          error_kind: "ws_error_frame",
          duration_ms: Date.now() - meta.startedAt,
          chunk_count: meta.chunkCount,
        });
        sendMetaRef.current = null;
      }
    }
  }, [lastMessage, markInterrupted, dispatch, readTurn]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        sendMessage();
      }
    },
    [sendMessage]
  );

  useEffect(() => {
    if (!isOpen) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    window.addEventListener("keydown", handleEsc);

    return () => window.removeEventListener("keydown", handleEsc);
  }, [isOpen, close]);

  const startNewChat = useCallback(() => {
    abortControllerRef.current?.abort();
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    const sid = readTurn().activeSessionId;
    if (sid) {
      canceledSessionIdsRef.current.add(sid);
      // Tell the BE to stop the Vertex stream too — otherwise chunks keep being
      // generated and billed even though the FE silently drops them.
      chatbotService.cancelStream({ session_id: sid }).catch(() => undefined);
    }
    // `setChatbotMessages` also releases the stream slot (activeSessionId/lastChunkIndex).
    dispatch(setChatbotMessages([buildIntroMessage(location.pathname)]));
    setInput("");
    setSessionMenuOpen(false);
    sendMetaRef.current = null;
    trackEvent(EVENTS.CHATBOT.NEW_CHAT, { page_route: location.pathname });
    setTimeout(() => inputRef.current?.focus(), 0);
  }, [buildIntroMessage, location.pathname, dispatch, readTurn]);

  useEffect(() => {
    if (!sessionMenuOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (sessionMenuRef.current && !sessionMenuRef.current.contains(e.target as Node)) {
        setSessionMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);

    return () => document.removeEventListener("mousedown", handleClick);
  }, [sessionMenuOpen]);

  return (
    <>
      {/* Outside the panel root on purpose: that root collapses to `w-0 overflow-hidden` when
          closed, so a chip rendered inside it would be invisible exactly when it is needed. */}
      {!isOpen && <GuideChip guide={guide} isMobile={isMobile} onOpen={() => dispatch(setChatbotOpen(true))} />}
      <div
        className={cn(
          "bg-black-100 overflow-hidden",
          isMobile
            ? cn("fixed inset-0 z-[60] w-full h-screen transition-transform duration-300 ease-out", isOpen ? "translate-y-0" : "translate-y-full pointer-events-none")
            : cn(
                "sticky top-0 h-screen shrink-0 transition-[width,border-color] duration-300 ease-out",
                isOpen ? "w-[420px] max-w-[100vw] border-l border-black-300" : "w-0 border-l-0 border-transparent"
              )
        )}
        aria-hidden={!isOpen}
      >
        <div className={cn("h-full flex flex-col", isMobile ? "w-full" : "w-[420px] max-w-[100vw]")}>
          <div className="shrink-0 flex items-center justify-between px-4 py-3 border-b border-black-300 relative">
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <div ref={sessionMenuRef} className="relative min-w-0 flex-1">
                <button onClick={() => setSessionMenuOpen((prev) => !prev)} className="flex items-center gap-1 max-w-full text-left rounded-md px-1 py-0.5 hover:bg-black-200 transition-all">
                  <span className="text-[13px] font-semibold text-white truncate">{t("chatbot.new_chat_title")}</span>
                  <ChevronDown size={13} className={cn("opacity-60 shrink-0 transition-transform", sessionMenuOpen && "rotate-180")} />
                </button>
                {sessionMenuOpen && (
                  <div className="absolute left-0 top-full mt-1 z-50 w-[300px] max-w-[calc(100vw-3rem)] rounded-lg border border-black-300 bg-black-100 shadow-xl overflow-hidden">
                    <div className="px-2 pt-2">
                      <button
                        onClick={startNewChat}
                        className="w-full flex items-center gap-2 px-3 py-2 rounded-md border border-dashed border-black-300 hover:border-purpleGreen hover:bg-black-200 text-white text-[13px] transition-all"
                      >
                        <Plus size={14} />
                        <span>{t("chatbot.new_chat")}</span>
                      </button>
                    </div>
                    <div className="mt-2 max-h-[320px] overflow-y-auto scrollbar-none">
                      <div className="px-4 py-6 text-center">
                        <MessageSquare className="w-4 h-4 text-grey/40 mx-auto mb-1.5" />
                        <p className="text-[11px] text-grey/70">{t("chatbot.no_sessions")}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <button onClick={startNewChat} className="h-7 w-7 flex items-center justify-center rounded-md text-grey hover:bg-black-200 hover:text-white transition-all" title={t("chatbot.new_chat")}>
                <RefreshCw size={13} />
              </button>
              <button onClick={close} className="h-7 w-7 flex items-center justify-center rounded-md text-grey hover:bg-black-200 hover:text-white transition-all" title={t("chatbot.close")}>
                <X size={14} />
              </button>
            </div>
          </div>

          {/* Pinned above the conversation, not inside it: the guide is ambient state, and a card
            scrolled away by a long answer would be the same as having no guide. */}
          <div className="shrink-0">
            <GuideCard guide={guide} onReturn={returnToGuide} onExit={exitGuide} />
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto scrollbar-none">
            <div className="flex flex-col gap-3 px-4 py-4">
              {messages.map((m) => {
                const showSuggestions = m.role === "assistant" && m.suggestions && m.suggestions.length > 0 && messages.length === 1;
                const showTyping = m.role === "assistant" && m.status === "streaming" && !m.text;
                const statusNote = m.role === "assistant" ? (m.status === "canceled" ? "Canceled" : m.status === "interrupted" ? "Response interrupted" : m.status === "error" ? "Error" : null) : null;

                return m.role === "user" ? (
                  <div key={m.id} className="flex justify-end">
                    <div className="max-w-[85%] px-3 py-2 rounded-2xl rounded-br-sm bg-purpleGreen/15 border border-purpleGreen/30 text-[12px] text-white leading-relaxed whitespace-pre-wrap break-words">
                      {m.text}
                    </div>
                  </div>
                ) : (
                  <div key={m.id} className="flex flex-col gap-2">
                    {(m.text || showTyping) && (
                      <div className="fab-bubble-in max-w-[90%] px-3 py-2 rounded-2xl rounded-bl-sm border text-[12px] leading-relaxed break-words bg-black-200/40 border-black-300 text-white">
                        {showTyping ? (
                          <span className="italic text-grey">{t("chatbot.thinking")}</span>
                        ) : (
                          <div className="max-w-none break-words [&_p]:my-1 [&_ul]:my-1 [&_ul]:list-disc [&_ul]:pl-4 [&_ol]:my-1 [&_ol]:list-decimal [&_ol]:pl-4 [&_li]:my-0 [&_strong]:font-semibold [&_code]:rounded [&_code]:bg-black-300 [&_code]:px-1 [&_code]:py-0.5 [&_code]:text-[11px] [&_a]:text-purpleGreen [&_a]:underline">
                            <ReactMarkdown
                              remarkPlugins={[remarkGfm]}
                              components={{
                                /* REF-6217 P1-5. The Capability Graph is the only navigation
                                   authority in Ask Refabric, so a link the MODEL wrote may never
                                   become an in-app destination. `messageLinks.tsx` holds the
                                   policy, what the rendering stack already covers, and why the
                                   renderer is a named component instead of an inline arrow.
                                   `linkTarget` is deliberately not set: that component owns
                                   target/rel too, so one place decides everything about a
                                   model-authored anchor. */
                                a: ChatbotProseLink,
                                img: () => null,
                              }}
                            >
                              {m.text}
                            </ReactMarkdown>
                          </div>
                        )}
                      </div>
                    )}
                    {statusNote && <div className="text-[11px] italic text-grey pl-1">{statusNote}</div>}
                    {/* Review capture (REF-6217 P1-0). Allowlisted accounts only — `reviewEligible`
                      comes from the BE, which re-checks on create. `isReviewable` keeps it off the
                      intro bubble, a streaming bubble, and anything with no question behind it. */}
                    {reviewEligible && isReviewable(messages, m.id) && (
                      <div className="flex w-fit items-center gap-2">
                        <button
                          onClick={() => captureForReview(m.id)}
                          disabled={capturingId === m.id}
                          data-testid={`chatbot-review-capture-${m.id}`}
                          className="flex w-fit items-center gap-1.5 rounded-md px-2 py-1 text-[11px] text-grey transition-all hover:bg-black-200 hover:text-white disabled:opacity-50"
                          title={t("chatbot.review_link_hint")}
                        >
                          {capturingId === m.id ? <Loader2 size={11} className="animate-spin" /> : <Link2 size={11} />}
                          <span>{t("chatbot.review_link")}</span>
                        </button>
                        {/* The confirmation. Persistent rather than a 2s flash: the reviewer's next
                          action is to paste the link somewhere, and a message that disappears while
                          they switch windows is the same as no message. Cleared when they capture
                          again. */}
                        {captureResult?.id === m.id && (
                          <span
                            data-testid={`chatbot-review-result-${m.id}`}
                            className={cn("flex items-center gap-1 text-[11px]", captureResult.state === "failed" ? "text-red" : "text-primary")}
                            role="status"
                          >
                            {captureResult.state === "failed" ? <AlertCircle size={11} /> : <Check size={11} />}
                            <span>
                              {captureResult.state === "copied" ? t("chatbot.review_link_copied") : captureResult.state === "id-only" ? t("chatbot.review_id_copied") : t("chatbot.review_link_failed")}
                            </span>
                          </span>
                        )}
                      </div>
                    )}
                    {visibleActionsFor(m).map((a) => {
                      // A guided flow this build knows AND this user can reach (REF-6217 T2.4).
                      // Null in every degraded case, which renders the destination button alone —
                      // exactly today's behaviour.
                      const guided = guidedFlowFor(a);

                      return (
                        <div key={a.capability_id} className="flex flex-wrap items-center gap-2">
                          {guided && (
                            <button
                              onClick={() => startGuidedFlow(a)}
                              data-testid={`chatbot-guide-${a.capability_id}`}
                              className="flex w-fit max-w-full items-center gap-2 rounded-lg border border-purpleGreen/40 bg-purpleGreen/10 px-3 py-2 text-left text-[12px] text-white transition-all hover:bg-purpleGreen/20"
                            >
                              <Compass size={13} className="shrink-0 text-purpleGreen" />
                              {/* Generic on purpose: the destination is named by the button beside
                                  it, and the offer reads the same for every future flow. */}
                              <span className="truncate">{t("chatbot.guide_action")}</span>
                            </button>
                          )}
                          <button
                            onClick={() => goToCapability(a)}
                            data-testid={`chatbot-nav-${a.capability_id}`}
                            className={cn(
                              "flex w-fit max-w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-[12px] text-white transition-all",
                              // Secondary once a guide is on offer — the guided run is the new
                              // primary intent, and plain navigation stays one tap away.
                              guided ? "border border-black-300 hover:bg-black-200" : "border border-purpleGreen/40 bg-purpleGreen/10 hover:bg-purpleGreen/20"
                            )}
                          >
                            {/* Both labels come verbatim from the capability graph — the exact text on
                              the user's screen in their language. Never wrap either in t().
                              `destination_label` names where this button actually lands, which is not
                              the capability the answer discusses whenever that capability is a tool
                              inside a page: the prose says Recolor, the button says Design Canvas.
                              Absent (older BE, or a destination that IS the capability) → `label`. */}
                            <span className="truncate">{t("chatbot.nav_action", { label: a.destination_label ?? a.label })}</span>
                            <ArrowRight size={13} className={cn("shrink-0", guided ? "text-grey" : "text-purpleGreen")} />
                          </button>
                        </div>
                      );
                    })}
                    {showSuggestions && m.suggestions && (
                      <div className="mt-1 flex flex-col gap-1.5">
                        {m.suggestions.map((s) => (
                          <button
                            key={s.labelKey}
                            onClick={() => sendMessage(s.query)}
                            className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-left text-[13px] text-white hover:bg-black-200 transition-all border border-black-300"
                          >
                            <span className="text-grey">
                              <Sparkles size={14} />
                            </span>
                            <span className="truncate">{t(s.labelKey)}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="shrink-0 border-t border-black-300 px-3 py-2.5">
            <div className="flex items-end gap-2">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={t("chatbot.input_placeholder")}
                rows={1}
                className="flex-1 resize-none border border-black-300 rounded-lg bg-black-200 px-3 py-2 text-[13px] leading-relaxed text-white placeholder:text-grey focus:outline-none focus:border-purpleGreen scrollbar-none min-h-[40px] max-h-[140px]"
              />
              {isStreaming ? (
                <button
                  onClick={cancelStream}
                  className="h-10 w-10 flex items-center justify-center rounded-lg bg-orange text-trueWhite hover:opacity-80 transition-all shrink-0"
                  title={t("chatbot.minimize")}
                >
                  <Loader2 className="w-4 h-4 animate-spin" />
                </button>
              ) : (
                <button
                  onClick={() => sendMessage()}
                  disabled={!input.trim()}
                  className="h-10 w-10 flex items-center justify-center rounded-lg bg-purpleGreen text-white disabled:opacity-40 disabled:cursor-not-allowed hover:bg-purpleGreen/90 transition-all shrink-0"
                  title={t("chatbot.send")}
                >
                  <Send className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default DashboardChatbot;
